import React, { useMemo } from "react";
import PropTypes from "prop-types";
import {
    Box,
    Chip,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    makeStyles,
} from "@material-ui/core";

const useStyles = makeStyles((theme) => ({
    root: {
        width: "100%",
    },
    titleContainer: {
        marginBottom: theme.spacing(2),
    },
    title: {
        fontWeight: 700,
    },
    subtitle: {
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(0.5),
    },
    tableContainer: {
        width: "100%",
        overflowX: "auto",
    },
    tableHead: {
        backgroundColor: "#ef4123",
    },
    tableHeadCell: {
        color: "#ffffff",
        fontWeight: 700,
        whiteSpace: "nowrap",
    },
    positionCell: {
        width: 105,
        textAlign: "center",
        fontWeight: 700,
    },
    teamCell: {
        minWidth: 160,
        fontWeight: 700,
    },
    modalityCell: {
        minWidth: 130,
    },
    categoryCell: {
        minWidth: 140,
    },
    numericCell: {
        textAlign: "center",
        whiteSpace: "nowrap",
    },
    timeCell: {
        minWidth: 145,
        textAlign: "center",
        fontWeight: 700,
        fontFamily: "monospace",
        whiteSpace: "nowrap",
    },
    athletesCell: {
        minWidth: 320,
    },
    athleteItem: {
        marginBottom: theme.spacing(0.5),
    },
    selectedAthlete: {
        fontWeight: 700,
    },
    firstPlace: {
        backgroundColor: "rgba(239, 65, 35, 0.08)",
    },
    incompleteRow: {
        backgroundColor: "rgba(0, 0, 0, 0.04)",
    },
    incompleteText: {
        color: theme.palette.text.secondary,
        fontWeight: 600,
    },
    noPositionText: {
        color: theme.palette.text.secondary,
        fontWeight: 700,
    },
    emptyContainer: {
        padding: theme.spacing(4),
        textAlign: "center",
    },
    chip: {
        fontWeight: 700,
    },
}));

const timeStringToMilliseconds = (time) => {
    if (time === null || time === undefined || time === "") {
        return null;
    }

    const normalizedTime = String(time).trim();

    if (!normalizedTime) {
        return null;
    }

    const parts = normalizedTime.split(":");

    if (parts.length !== 3) {
        return null;
    }

    const hours = Number(parts[0]);
    const minutes = Number(parts[1]);
    const secondsWithMilliseconds = Number(parts[2]);

    if (
        !Number.isFinite(hours) ||
        !Number.isFinite(minutes) ||
        !Number.isFinite(secondsWithMilliseconds)
    ) {
        return null;
    }

    if (
        hours < 0 ||
        minutes < 0 ||
        minutes >= 60 ||
        secondsWithMilliseconds < 0 ||
        secondsWithMilliseconds >= 60
    ) {
        return null;
    }

    return Math.round(
        hours * 60 * 60 * 1000 +
        minutes * 60 * 1000 +
        secondsWithMilliseconds * 1000
    );
};

const millisecondsToTimeString = (
    milliseconds,
    showMilliseconds = false
) => {
    if (
        milliseconds === null ||
        milliseconds === undefined ||
        !Number.isFinite(milliseconds) ||
        milliseconds < 0
    ) {
        return showMilliseconds
            ? "00:00:00.000"
            : "00:00:00";
    }

    const roundedMilliseconds = Math.round(milliseconds);
    const millisecondsPart = roundedMilliseconds % 1000;
    const totalSeconds = Math.floor(roundedMilliseconds / 1000);

    const seconds = totalSeconds % 60;
    const totalMinutes = Math.floor(totalSeconds / 60);
    const minutes = totalMinutes % 60;
    const hours = Math.floor(totalMinutes / 60);

    const formattedTime = [
        String(hours).padStart(2, "0"),
        String(minutes).padStart(2, "0"),
        String(seconds).padStart(2, "0"),
    ].join(":");

    if (!showMilliseconds) {
        return formattedTime;
    }

    return `${formattedTime}.${String(millisecondsPart).padStart(
        3,
        "0"
    )}`;
};

/**
 * Orden solicitado:
 *
 * 1. toMS
 * 2. to
 * 3. tcMS
 * 4. tc
 */
const getAthleteTime = (result) => {
    if (!result || typeof result !== "object") {
        return null;
    }

    const timeFields = [
        { field: "toMS", value: result.toMS },
        { field: "to", value: result.to },
        { field: "tcMS", value: result.tcMS },
        { field: "tc", value: result.tc },
    ];

    for (let index = 0; index < timeFields.length; index += 1) {
        const currentTime = timeFields[index];

        const milliseconds = timeStringToMilliseconds(
            currentTime.value
        );

        if (milliseconds !== null) {
            return {
                milliseconds,
                originalValue: currentTime.value,
                sourceField: currentTime.field,
            };
        }
    }

    return null;
};

const hasValidRaceTime = (athleteTime) => {
    return Boolean(
        athleteTime &&
        Number.isFinite(athleteTime.milliseconds) &&
        athleteTime.milliseconds > 0
    );
};

const normalizeValue = (value) => {
    if (value === null || value === undefined) {
        return null;
    }

    const normalizedValue = String(value).trim();

    return normalizedValue || null;
};

const normalizeKey = (value) => {
    const normalizedValue = normalizeValue(value);

    return normalizedValue
        ? normalizedValue.toLocaleLowerCase()
        : null;
};

const getModalityData = (result) => {
    const modalityId =
        result.codMod !== null && result.codMod !== undefined
            ? result.codMod
            : result.mod !== null && result.mod !== undefined
                ? result.mod
                : result.modalidad;

    if (
        modalityId === null ||
        modalityId === undefined ||
        modalityId === ""
    ) {
        return null;
    }

    const modalityName =
        normalizeValue(result.nombreModalidad) ||
        normalizeValue(result.modalidad) ||
        normalizeValue(result.mod) ||
        `Modalidad ${modalityId}`;

    return {
        id: modalityId,
        key: normalizeKey(modalityId),
        name: modalityName,
    };
};

const getCategoryData = (result) => {
    const categoryId =
        result.codCat !== null && result.codCat !== undefined
            ? result.codCat
            : result.cat;

    const categoryName =
        normalizeValue(result.cat) ||
        normalizeValue(result.nombreCategoria) ||
        (categoryId !== null && categoryId !== undefined
            ? `Categoría ${categoryId}`
            : null);

    if (!categoryName) {
        return {
            id: null,
            key: null,
            name: "Sin categoría",
        };
    }

    return {
        id: categoryId,
        key: normalizeKey(
            categoryId !== null && categoryId !== undefined
                ? categoryId
                : categoryName
        ),
        name: categoryName,
    };
};

const assignPositions = (items, positionProperty) => {
    return items.map((item, index, sortedItems) => {
        let position = index + 1;

        if (
            index > 0 &&
            item.totalMilliseconds ===
            sortedItems[index - 1].totalMilliseconds
        ) {
            position =
                sortedItems[index - 1][positionProperty];
        }

        return {
            ...item,
            [positionProperty]: position,
        };
    });
};

const ResultadosPorTeam = ({
    resultados,
    cantidadAtletasPorEquipo,
    excluirBorrados,
    excluirDescalificados,
    mostrarMilisegundos,
    mostrarAtletas,
    titulo,
}) => {
    const classes = useStyles();

    const requiredAthletes = Math.max(
        1,
        Number(cantidadAtletasPorEquipo) || 1
    );

    const teams = useMemo(() => {
        const resultList = Array.isArray(resultados)
            ? resultados
            : Object.values(resultados || {});

        const teamsMap = {};

        resultList.forEach((result) => {
            if (!result || typeof result !== "object") {
                return;
            }

            if (excluirBorrados && result.borrado === true) {
                return;
            }

            const notes =
                result.extraData &&
                    Array.isArray(result.extraData.notes)
                    ? result.extraData.notes
                    : [];

            const isDisqualified = notes.some((note) => {
                const description = String(
                    note && note.description
                        ? note.description
                        : ""
                ).toLowerCase();

                return (
                    description.includes("descalificado") ||
                    description.includes("descalificada") ||
                    description.includes("disqualified")
                );
            });

            if (excluirDescalificados && isDisqualified) {
                return;
            }

            const teamName = normalizeValue(
                result.extraData && result.extraData.team
            );

            if (!teamName) {
                return;
            }

            const modality = getModalityData(result);

            if (!modality) {
                return;
            }

            const athleteTime = getAthleteTime(result);

            const timeData = athleteTime || {
                milliseconds: 0,
                originalValue: null,
                sourceField: null,
            };

            const validTime = hasValidRaceTime(timeData);
            const category = getCategoryData(result);
            const teamKey = normalizeKey(teamName);

            const groupKey = `${teamKey}__${modality.key}`;

            if (!teamsMap[groupKey]) {
                teamsMap[groupKey] = {
                    groupKey,
                    team: teamName,
                    modalityId: modality.id,
                    modalityKey: modality.key,
                    modalityName: modality.name,
                    athletes: [],
                };
            }

            teamsMap[groupKey].athletes.push({
                numero: result.num,
                nombre:
                    result.nombre ||
                    `Atleta ${result.num || ""}`,
                categoryId: category.id,
                categoryKey: category.key,
                categoryName: category.name,
                timeMilliseconds: timeData.milliseconds,
                timeValue: timeData.originalValue,
                timeSource: timeData.sourceField,
                hasValidTime: validTime,
            });
        });

        const processedTeams = Object.values(teamsMap).map(
            (team) => {
                /**
                 * Lista para mostrar:
                 *
                 * 1. Atletas con tiempo, ordenados por tiempo.
                 * 2. Atletas sin tiempo, ordenados por nombre.
                 */
                const displayAthletes = [...team.athletes].sort(
                    (athleteA, athleteB) => {
                        if (
                            athleteA.hasValidTime !==
                            athleteB.hasValidTime
                        ) {
                            return athleteA.hasValidTime ? -1 : 1;
                        }

                        if (
                            athleteA.hasValidTime &&
                            athleteB.hasValidTime &&
                            athleteA.timeMilliseconds !==
                            athleteB.timeMilliseconds
                        ) {
                            return (
                                athleteA.timeMilliseconds -
                                athleteB.timeMilliseconds
                            );
                        }

                        return athleteA.nombre.localeCompare(
                            athleteB.nombre
                        );
                    }
                );

                /**
                 * Solo los atletas con tiempo mayor a cero
                 * participan en el cálculo.
                 */
                const athletesWithValidTime =
                    team.athletes.filter(
                        (athlete) => athlete.hasValidTime
                    );

                const sortedAthletes = [
                    ...athletesWithValidTime,
                ].sort(
                    (athleteA, athleteB) =>
                        athleteA.timeMilliseconds -
                        athleteB.timeMilliseconds
                );

                const selectedAthletes = sortedAthletes.slice(
                    0,
                    requiredAthletes
                );

                const selectedAthleteKeys = new Set(
                    selectedAthletes.map(
                        (athlete) =>
                            `${athlete.numero}__${athlete.nombre}`
                    )
                );

                const totalMilliseconds =
                    selectedAthletes.reduce(
                        (total, athlete) =>
                            total + athlete.timeMilliseconds,
                        0
                    );

                const isClassified =
                    sortedAthletes.length >= requiredAthletes &&
                    selectedAthletes.length === requiredAthletes &&
                    totalMilliseconds > 0;

                const selectedCategoryKeys = new Set(
                    selectedAthletes.map(
                        (athlete) => athlete.categoryKey
                    )
                );

                const hasSameCategory =
                    isClassified &&
                    selectedAthletes.length === requiredAthletes &&
                    selectedCategoryKeys.size === 1 &&
                    selectedAthletes[0].categoryKey !== null;

                const teamCategory = hasSameCategory
                    ? {
                        id: selectedAthletes[0].categoryId,
                        key: selectedAthletes[0].categoryKey,
                        name: selectedAthletes[0].categoryName,
                    }
                    : null;

                return {
                    ...team,
                    totalAthletes: team.athletes.length,
                    athletesWithValidTime: sortedAthletes.length,
                    displayAthletes,
                    selectedAthletes,
                    selectedAthleteKeys,
                    totalMilliseconds,
                    isClassified,
                    hasSameCategory,
                    categoryId: teamCategory
                        ? teamCategory.id
                        : null,
                    categoryKey: teamCategory
                        ? teamCategory.key
                        : null,
                    categoryName: teamCategory
                        ? teamCategory.name
                        : null,
                    modalityPosition: 0,
                    categoryPosition: 0,
                };
            }
        );

        /**
         * Posición por modalidad.
         */
        const classifiedByModality = {};

        processedTeams
            .filter(
                (team) =>
                    team.isClassified &&
                    team.totalMilliseconds > 0
            )
            .forEach((team) => {
                if (!classifiedByModality[team.modalityKey]) {
                    classifiedByModality[team.modalityKey] = [];
                }

                classifiedByModality[team.modalityKey].push(team);
            });

        const modalityPositionMap = {};

        Object.values(classifiedByModality).forEach(
            (modalityTeams) => {
                const sortedTeams = [...modalityTeams].sort(
                    (teamA, teamB) => {
                        if (
                            teamA.totalMilliseconds !==
                            teamB.totalMilliseconds
                        ) {
                            return (
                                teamA.totalMilliseconds -
                                teamB.totalMilliseconds
                            );
                        }

                        return teamA.team.localeCompare(teamB.team);
                    }
                );

                const positionedTeams = assignPositions(
                    sortedTeams,
                    "modalityPosition"
                );

                positionedTeams.forEach((team) => {
                    modalityPositionMap[team.groupKey] =
                        team.modalityPosition;
                });
            }
        );

        /**
         * Posición por categoría.
         *
         * La clasificación es independiente por:
         *
         * modalidad + categoría
         */
        const classifiedByCategory = {};

        processedTeams
            .filter(
                (team) =>
                    team.isClassified &&
                    team.hasSameCategory &&
                    team.totalMilliseconds > 0
            )
            .forEach((team) => {
                const categoryGroupKey =
                    `${team.modalityKey}__${team.categoryKey}`;

                if (!classifiedByCategory[categoryGroupKey]) {
                    classifiedByCategory[categoryGroupKey] = [];
                }

                classifiedByCategory[categoryGroupKey].push(team);
            });

        const categoryPositionMap = {};

        Object.values(classifiedByCategory).forEach(
            (categoryTeams) => {
                const sortedTeams = [...categoryTeams].sort(
                    (teamA, teamB) => {
                        if (
                            teamA.totalMilliseconds !==
                            teamB.totalMilliseconds
                        ) {
                            return (
                                teamA.totalMilliseconds -
                                teamB.totalMilliseconds
                            );
                        }

                        return teamA.team.localeCompare(teamB.team);
                    }
                );

                const positionedTeams = assignPositions(
                    sortedTeams,
                    "categoryPosition"
                );

                positionedTeams.forEach((team) => {
                    categoryPositionMap[team.groupKey] =
                        team.categoryPosition;
                });
            }
        );

        const teamsWithPositions = processedTeams.map(
            (team) => ({
                ...team,
                modalityPosition:
                    modalityPositionMap[team.groupKey] || 0,
                categoryPosition:
                    categoryPositionMap[team.groupKey] || 0,
            })
        );

        return teamsWithPositions.sort((teamA, teamB) => {
            const modalityComparison =
                teamA.modalityName.localeCompare(
                    teamB.modalityName
                );

            if (modalityComparison !== 0) {
                return modalityComparison;
            }

            if (
                teamA.isClassified !== teamB.isClassified
            ) {
                return teamA.isClassified ? -1 : 1;
            }

            if (
                teamA.modalityPosition > 0 &&
                teamB.modalityPosition > 0 &&
                teamA.modalityPosition !==
                teamB.modalityPosition
            ) {
                return (
                    teamA.modalityPosition -
                    teamB.modalityPosition
                );
            }

            if (
                teamA.modalityPosition > 0 &&
                teamB.modalityPosition === 0
            ) {
                return -1;
            }

            if (
                teamA.modalityPosition === 0 &&
                teamB.modalityPosition > 0
            ) {
                return 1;
            }

            if (
                teamA.totalMilliseconds !==
                teamB.totalMilliseconds
            ) {
                return (
                    teamA.totalMilliseconds -
                    teamB.totalMilliseconds
                );
            }

            return teamA.team.localeCompare(teamB.team);
        });
    }, [
        resultados,
        requiredAthletes,
        excluirBorrados,
        excluirDescalificados,
    ]);

    return (
        <Box className={classes.root}>
            <Box className={classes.titleContainer}>
                <Typography
                    variant="h5"
                    className={classes.title}
                >
                    {titulo}
                </Typography>

                <Typography
                    variant="body2"
                    className={classes.subtitle}
                >
                    Se suman los {requiredAthletes} mejores tiempos
                    válidos de cada equipo dentro de la misma
                    modalidad. Todos los integrantes se muestran,
                    aunque todavía no tengan tiempo.
                </Typography>
            </Box>

            <TableContainer
                component={Paper}
                className={classes.tableContainer}
            >
                {teams.length === 0 ? (
                    <Box className={classes.emptyContainer}>
                        <Typography variant="body1">
                            No hay participantes con equipo y modalidad.
                        </Typography>
                    </Box>
                ) : (
                    <Table aria-label="Resultados por equipo">
                        <TableHead className={classes.tableHead}>
                            <TableRow>
                                <TableCell
                                    className={`${classes.tableHeadCell} ${classes.positionCell}`}
                                >
                                    Pos. modalidad
                                </TableCell>

                                <TableCell
                                    className={`${classes.tableHeadCell} ${classes.positionCell}`}
                                >
                                    Pos. categoría
                                </TableCell>

                                <TableCell
                                    className={classes.tableHeadCell}
                                >
                                    Equipo
                                </TableCell>

                                <TableCell
                                    className={classes.tableHeadCell}
                                >
                                    Modalidad
                                </TableCell>

                                <TableCell
                                    className={classes.tableHeadCell}
                                >
                                    Categoría
                                </TableCell>

                                <TableCell
                                    className={`${classes.tableHeadCell} ${classes.numericCell}`}
                                >
                                    Integrantes
                                </TableCell>

                                {mostrarAtletas && (
                                    <TableCell
                                        className={classes.tableHeadCell}
                                    >
                                        Integrantes del equipo
                                    </TableCell>
                                )}

                                <TableCell
                                    className={`${classes.tableHeadCell} ${classes.timeCell}`}
                                >
                                    Tiempo total
                                </TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {teams.map((team) => (
                                <TableRow
                                    key={team.groupKey}
                                    hover
                                    className={
                                        !team.isClassified
                                            ? classes.incompleteRow
                                            : team.modalityPosition === 1
                                                ? classes.firstPlace
                                                : undefined
                                    }
                                >
                                    <TableCell
                                        className={classes.positionCell}
                                    >
                                        {team.modalityPosition > 0 ? (
                                            <Chip
                                                size="small"
                                                label={team.modalityPosition}
                                                className={classes.chip}
                                            />
                                        ) : (
                                            <Typography
                                                variant="body2"
                                                className={
                                                    classes.noPositionText
                                                }
                                            >
                                                0
                                            </Typography>
                                        )}
                                    </TableCell>

                                    <TableCell
                                        className={classes.positionCell}
                                    >
                                        {team.categoryPosition > 0 ? (
                                            <Chip
                                                size="small"
                                                label={team.categoryPosition}
                                                className={classes.chip}
                                            />
                                        ) : (
                                            <Typography
                                                variant="body2"
                                                className={
                                                    classes.noPositionText
                                                }
                                            >
                                                0
                                            </Typography>
                                        )}
                                    </TableCell>

                                    <TableCell
                                        className={classes.teamCell}
                                    >
                                        {team.team}
                                    </TableCell>

                                    <TableCell
                                        className={classes.modalityCell}
                                    >
                                        {team.modalityName}
                                    </TableCell>

                                    <TableCell
                                        className={classes.categoryCell}
                                    >
                                        {team.hasSameCategory
                                            ? team.categoryName
                                            : team.isClassified
                                                ? "Categoría mixta"
                                                : "—"}
                                    </TableCell>

                                    <TableCell
                                        className={classes.numericCell}
                                    >
                                        {team.totalAthletes}
                                    </TableCell>

                                    {mostrarAtletas && (
                                        <TableCell
                                            className={classes.athletesCell}
                                        >
                                            {team.displayAthletes.length > 0 ? (
                                                team.displayAthletes.map(
                                                    (athlete, index) => {
                                                        const athleteKey =
                                                            `${athlete.numero}__${athlete.nombre}`;

                                                        const isSelected =
                                                            team.selectedAthleteKeys.has(
                                                                athleteKey
                                                            );

                                                        return (
                                                            <Typography
                                                                key={`${team.groupKey}-${athlete.numero}-${index}`}
                                                                variant="body2"
                                                                className={`${classes.athleteItem} ${isSelected
                                                                    ? classes.selectedAthlete
                                                                    : ""
                                                                    }`}
                                                            >
                                                                {index + 1}. {athlete.nombre}

                                                                {athlete.numero !==
                                                                    undefined &&
                                                                    athlete.numero !== null &&
                                                                    ` (#${athlete.numero})`}



                                                                {" — "}

                                                                {athlete.hasValidTime
                                                                    ? millisecondsToTimeString(
                                                                        athlete.timeMilliseconds,
                                                                        mostrarMilisegundos
                                                                    )
                                                                    : "Sin tiempo"}

                                                                {isSelected
                                                                    ? " — Considerado"
                                                                    : ""}
                                                            </Typography>
                                                        );
                                                    }
                                                )
                                            ) : (
                                                <Typography
                                                    variant="body2"
                                                    className={
                                                        classes.incompleteText
                                                    }
                                                >
                                                    Sin integrantes
                                                </Typography>
                                            )}
                                        </TableCell>
                                    )}

                                    <TableCell
                                        className={classes.timeCell}
                                    >
                                        {millisecondsToTimeString(
                                            team.totalMilliseconds,
                                            mostrarMilisegundos
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </TableContainer>
        </Box>
    );
};

ResultadosPorTeam.propTypes = {
    resultados: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.object),
        PropTypes.object,
    ]),
    cantidadAtletasPorEquipo: PropTypes.number,
    excluirBorrados: PropTypes.bool,
    excluirDescalificados: PropTypes.bool,
    mostrarMilisegundos: PropTypes.bool,
    mostrarAtletas: PropTypes.bool,
    titulo: PropTypes.string,
};

ResultadosPorTeam.defaultProps = {
    resultados: {},
    cantidadAtletasPorEquipo: 3,
    excluirBorrados: false,
    excluirDescalificados: false,
    mostrarMilisegundos: true,
    mostrarAtletas: true,
    titulo: "Resultados por equipo",
};

export default ResultadosPorTeam;